import useSWR from 'swr';
import { useToken } from '@/app/hooks';
import API from '@/utils/api';
import { useCallback, useMemo, useState } from 'react';
import { MissionCode } from '@/app/pages/RewardPage/utils';

export enum CategoryType {
  ONBOARDING = 'new_user',
  TRADING = 'deposit_trade',
  FEATURE_EXPERIENCE = 'feature_mission',
  DAILY = 'daily_mission',
}

export enum MissionType {
  ONCE = 'once',
  MONTHLY = 'monthly',
  PERMANENT = 'permanent',
}

export enum MissionStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ENDED = 'ended',
}

export interface MissionResponse<T> extends DefaultResponse {
  data: T;
  error: DefaultResponse;
}

export interface DefaultResponse {
  success: boolean;
  code: string;
  message: string;
}

// Tier 타입 정의 (미션별로 다른 필드 구조)
export interface ReferralTier {
  minDeposit: string; // 친구초대 전용: 최소 입금액
  minVolume: string; // 친구초대 전용: 최소 거래량
  rewardAmount: string;
}

export interface StandardTier {
  minAmount?: string; // 일반 미션: 최소 금액
  minLeverage?: string; // 레버리지 미션: 최소 레버리지
  minVolume?: string; // 레버리지 미션: 최소 거래량
  streakDays?: number; // 출석 미션: 연속 일수
  rewardAmount: string;
}

// 레퍼럴 미션 - 친구별 진행 상황
export interface ReferralFriend {
  userId: number;
  totalDeposit: number;
  totalVolume: number;
  achievedTiers: number[];
  claimedTiers: number[];
}

// 레퍼럴 미션 전용 metadata 타입 (실제 백엔드 응답 구조)
export interface ReferralMetadata {
  referrals: ReferralFriend[];
  lastClaimedAt?: string;
}

// 일반 미션 metadata 타입
export interface StandardMetadata {
  currentStreak?: number;
  achievedTiers?: number[];
  availableTiers?: number[];
  claimedTiers?: number[];
  totalTiers?: number;
  [key: string]: any;
}

// Mission conditions 타입
export interface MissionConditions {
  action:
    | 'referral'
    | 'first_deposit'
    | 'net_deposit'
    | 'net_volume'
    | 'period_goal'
    | 'leverage_trade'
    | 'attendance'
    | string;
  tiers?: ReferralTier[] | StandardTier[]; // 미션별로 다른 tier 구조
  steps?: any[]; // 단계별 조건
  [key: string]: any; // 기타 조건
}

export interface Mission {
  id: number;
  code: MissionCode;
  name: string;
  description: string;
  type: MissionType;
  category: CategoryType;
  status: string;
  conditions: MissionConditions;
  userProgress?: {
    status: string;
    canClaim: boolean;
    startedAt: string | null;
    completedAt: string | null;
    claimedAt: string | null;
    progress: {
      percentage: number;
      current?: number;
      target?: number;
    };
    achievedTiers?: number[];
    availableTiers?: number[];
    claimedTiers?: number[];
    totalTiers?: number;
    metadata?: ReferralMetadata | StandardMetadata;
  };
  rewards: {
    type: string;
    amount: string;
  };
  rewardAmount: string;
  displayOrder: number;
  isVisible: boolean;
  resetPeriod: string;
  metadata: any;
  createdAt: string;
  updatedAt: string;
  startDate: string;
  endDate: string;
}

export interface MissionCategory {
  id: number;
  name: string;
  description: string;
  displayOrder: number;
  isVisible: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// 미션 목록 조회 응답 인터페이스
export interface MissionListResponse {
  missions: Mission[];
  pagination: Pagination;
  filters: any;
}

// 미션 보상 수령 응답 인터페이스
export interface MssionRewardResponse {
  missionId: number;
  verificationToken: string;
}

export interface MissionProgressResponse {
  missions: Mission[];
  stats: {
    totalMissions: number;
    completedMissions: number;
    inProgressMissions: number;
    availableRewards: number;
    totalEarned: number;
    totalClaimed: number;
    currentMonthEarnings: number;
    streakDays: number;
    favoriteCategory: string;
    completionRate: number;
  };
  paginations: Pagination;
}

// 총 보상금액 조회 응답 인터페이스
export interface TotalRewardsResponse {
  totalRewards: number;
  missionCount: number;
  categories: string[];
}

import { NEW_API_URL } from '@/app/constants/APIKEYS';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useRewardAnimation } from '@/app/pages/RewardPage/useRewardAnimation';

export const BACKUP_API_URL = NEW_API_URL;

// 미션 상세 조회 훅 (useSWR 사용, 3초 캐싱)
export const useMissionDetail = (missionId: number | null | undefined) => {
  const { data: token } = useToken();

  // 토큰이 있으면 인증된 API, 없으면 public API 사용
  const KEY = missionId
    ? token
      ? `/missions/${missionId}`
      : `/missions/public/${missionId}`
    : null;

  const { data, error, isLoading, mutate } = useSWR<Mission>(
    KEY,
    async () => {
      if (!missionId) return null as any;

      // 토큰이 있으면 인증된 요청, 없으면 public 요청
      if (token) {
        const res = (await API.get(
          KEY!,
          token,
          {},
          false,
          BACKUP_API_URL
        )) as Mission;
        return res;
      } else {
        const res = (await API.send(
          'GET',
          KEY!,
          null,
          {},
          false,
          BACKUP_API_URL
        )) as Mission;
        return res;
      }
    },
    {
      dedupingInterval: 3000, // 3초 동안 중복 요청 방지
      revalidateOnFocus: false, // 포커스 시 재검증 비활성화
      revalidateOnReconnect: false, // 재연결 시 재검증 비활성화
      fallbackData: null as any, // 초기값을 null로 설정하여 undefined 방지
    }
  );

  return {
    data: (data ?? null) as Mission | null,
    error,
    isLoading,
    mutate,
  };
};

// 총 보상금액 조회 훅 (useSWR 사용, 캐싱)
export const useMissionTotalRewards = (
  category?: CategoryType | CategoryType[]
) => {
  const { data: token } = useToken();

  // 카테고리 파라미터 처리
  const KEY = useMemo(() => {
    let endpoint = token
      ? '/missions/total-rewards'
      : '/missions/public/total-rewards';

    if (category) {
      const categoryParam = Array.isArray(category)
        ? category.join(',')
        : category;
      endpoint += `?category=${encodeURIComponent(categoryParam)}`;
    }

    return endpoint;
  }, [category, token]);

  const { data, error, isLoading, mutate } = useSWR<TotalRewardsResponse>(
    KEY,
    async () => {
      // 토큰이 있으면 인증된 요청, 없으면 public 요청
      if (token) {
        const res = await API.get(KEY, token, {}, false, BACKUP_API_URL);
        return res;
      } else {
        const res = await API.send('GET', KEY, null, {}, false, BACKUP_API_URL);
        return res;
      }
    },
    {
      dedupingInterval: 5000, // 5초 동안 중복 요청 방지
      revalidateOnFocus: false, // 포커스 시 재검증 비활성화
      revalidateOnReconnect: false, // 재연결 시 재검증 비활성화
    }
  );

  return {
    data: data ?? null,
    error,
    isLoading,
    mutate,
  };
};

export const useTradeMission = (category?: CategoryType | CategoryType[]) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(30); // 카테고리별 필터링으로 limit 축소
  const [type, setType] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [sortBy, setSortBy] = useState<'displayOrder' | ''>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | ''>('');
  const { t } = useTranslation();
  const { showAnimation } = useRewardAnimation();

  const { data: token } = useToken();

  // page와 limit은 항상 포함, 나머지는 값이 있을 때만 쿼리파라미터에 포함
  // 로그인 상태에 따라 다른 엔드포인트 사용
  const KEY = useMemo(() => {
    const params: Record<string, string | number> = {
      page,
      limit,
    };
    // 카테고리 파라미터 처리: 배열이면 쉼표로 구분, 단일이면 그대로
    if (category) {
      params.category = Array.isArray(category) ? category.join(',') : category;
    }
    if (type) params.type = type;
    if (status) params.status = status;
    if (sortBy) params.sortBy = sortBy;
    if (sortOrder) params.sortOrder = sortOrder;

    const queryString = Object.entries(params)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    // 토큰이 있으면 인증된 API, 없으면 public API 사용
    const endpoint = token ? '/missions' : '/missions/public';
    return `${endpoint}?${queryString}`;
  }, [page, limit, category, type, status, sortBy, sortOrder, token]);

  // 미션 목록 조회
  const { data, mutate, isLoading } = useSWR<MissionListResponse>(
    KEY,
    async () => {
      // 토큰이 있으면 인증된 요청, 없으면 public 요청
      if (token) {
        const res = await API.get(KEY, token, {}, false, BACKUP_API_URL);
        return res;
      } else {
        const res = await API.send('GET', KEY, null, {}, false, BACKUP_API_URL);
        return res;
      }
    }
  );

  // 카테고리별로 미션 데이터를 그룹화
  const missionsByCategory = useMemo(() => {
    if (!data?.missions) return {};

    const grouped: Record<CategoryType, Mission[]> = {
      [CategoryType.ONBOARDING]: [],
      [CategoryType.TRADING]: [],
      [CategoryType.FEATURE_EXPERIENCE]: [],
      [CategoryType.DAILY]: [],
    };

    data.missions.forEach((mission) => {
      if (mission.category && grouped[mission.category as CategoryType]) {
        grouped[mission.category as CategoryType].push(mission);
      }
    });

    return grouped;
  }, [data]);

  // 특정 카테고리의 미션만 가져오기
  const getMissionsByCategory = useCallback(
    (category: CategoryType): Mission[] => {
      return missionsByCategory[category] || [];
    },
    [missionsByCategory]
  );

  // 모든 카테고리의 미션 데이터
  const getAllMissions = useMemo(() => {
    return data?.missions || [];
  }, [data]);

  // 카테고리별 미션 개수
  const getMissionCountByCategory = useCallback(
    (category: CategoryType): number => {
      return missionsByCategory[category]?.length || 0;
    },
    [missionsByCategory]
  );

  // 전체 미션 개수
  const getTotalMissionCount = useMemo(() => {
    return getAllMissions.length;
  }, [getAllMissions]);

  // 미션 목록 조회 파라미터 설정 객체
  const setParameters = useMemo(() => {
    return {
      setPage,
      setLimit,
      setType,
      setStatus,
      setSortBy,
      setSortOrder,
    };
  }, [setPage, setLimit, setType, setStatus, setSortBy, setSortOrder]);

  // 미션 보상 수령
  const getMissionReward = useCallback(
    async (
      missionId: number,
      tierIndex?: number,
      rewardAmount?: string | number,
      referralUserId?: number
    ) => {
      // ✅ 인증 체크: 토큰이 없으면 로그인 페이지로 리다이렉트
      if (!token) {
        message.error(t('pleaseLogin') || 'Please login to continue');
        window.location.href = '/login';
        return;
      }

      // tierIndex와 referralUserId를 query parameter로 전달합니다.
      let KEY = `/missions/${missionId}/claim`;
      const params: string[] = [];

      if (tierIndex !== undefined) {
        params.push(`tier=${tierIndex}`);
      }
      if (referralUserId !== undefined) {
        params.push(`referralUserId=${referralUserId}`);
      }

      if (params.length > 0) {
        KEY += `?${params.join('&')}`;
      }

      try {
        const res: any = await API.post(KEY, token, {}, BACKUP_API_URL);

        // success 필드 체크
        if (res?.success === false) {
          const errorMessage = res?.message || res?.error?.message;
          throw new Error(errorMessage);
        }

        const amount =
          rewardAmount || res?.data?.rewardAmount || res?.rewardAmount || '0';
        showAnimation({
          amount,
          currency: 'USDT',
        });

        mutate();
        return res;
      } catch (error: any) {
        const errorMessage = error?.message || error?.response?.data?.message;
        message.error(errorMessage || t('missionRewardFailed'));
        throw error;
      }
    },
    [token, mutate, t, showAnimation]
  );

  // 미션 상세 조회 (레거시 - useMissionDetail 사용 권장)
  const getMissionDetail = useCallback(
    async (missionId: number) => {
      // 토큰이 있으면 인증된 API, 없으면 public API 사용
      const KEY = token
        ? `/missions/${missionId}`
        : `/missions/public/${missionId}`;

      try {
        // 토큰이 있으면 인증된 요청, 없으면 public 요청
        if (token) {
          const res = (await API.get(
            KEY,
            token,
            {},
            false,
            BACKUP_API_URL
          )) as Mission;
          return res;
        } else {
          const res = (await API.send(
            'GET',
            KEY,
            null,
            {},
            false,
            BACKUP_API_URL
          )) as Mission;
          return res;
        }
      } catch (error) {
        console.error('미션 상세 조회 API 에러', error);
        throw error;
      }
    },
    [token]
  );

  // 미션 참가
  const startMission = useCallback(
    async (missionId: string) => {
      // ✅ 인증 체크: 토큰이 없으면 로그인 페이지로 리다이렉트
      if (!token) {
        message.error(t('pleaseLogin') || 'Please login to continue');
        window.location.href = '/login';
        return;
      }

      const KEY = `/missions/${missionId}/join`;

      try {
        const res = await API.post(KEY, token, {}, BACKUP_API_URL);
        return res;
      } catch (error) {
        console.error('미션 시작 API 에러', error);
        return error;
      }
    },
    [token, t]
  );

  const handleErrorCode = useCallback(
    (error: any) => {
      const errorCode = error?.code;

      switch (errorCode) {
        case 'DAILY_CONDITION_NOT_MET':
          return t('dailyConditionNotMet');
        case 'ALREADY_CHECKED_IN':
          return t('alreadyCheckedIn');
        default:
          return t('checkInFailed');
      }
    },
    [t]
  );

  // 출석 체크
  const checkInMission = useCallback(
    async (missionId: number) => {
      // ✅ 인증 체크: 토큰이 없으면 로그인 페이지로 리다이렉트
      if (!token) {
        message.error(t('pleaseLogin') || 'Please login to continue');
        window.location.href = '/login';
        return;
      }

      const KEY = `/missions/${missionId}/check-in`;

      try {
        const res: any = await API.post(KEY, token, {}, BACKUP_API_URL);

        // success 필드 체크
        if (res?.success === false) {
          throw res.error;
        }

        showAnimation({
          amount: res.reward.amount,
          currency: 'USDT',
        });
        mutate(); // 미션 목록 갱신
        return res;
      } catch (error: any) {
        message.error(handleErrorCode(error));
        throw error;
      }
    },
    [token, mutate, t]
  );

  return {
    data,
    isLoading,
    mutate,
    setParameters,
    getMissionReward,
    getMissionDetail,
    // 새로운 카테고리별 데이터 접근 함수들
    missionsByCategory,
    getMissionsByCategory,
    getAllMissions,
    getMissionCountByCategory,
    getTotalMissionCount,
    startMission,
    checkInMission,
  };
};
